import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";

const PRIMARY_SCORE_FILES = ["scores/rubric_scores_1.json", "scores/rubric_scores_2.json"];
const TIE_BREAKER_SCORE_FILES = ["scores/rubric_scores_3.json", "scores/rubric_scores_4.json"];

const SCORE_LABELS = {
  composition_framing: "Composition & Framing",
  realism_immersion: "Realism / Immersion",
  technical_quality: "Technical Quality",
  lighting_colour: "Lighting & Colour",
  creativity_originality: "Creativity / Originality",
  emotion_impact: "Emotion / Impact",
};

const scoreKeys = Object.keys(SCORE_LABELS);

function publicUrl(path) {
  const base = process.env.PUBLIC_URL || "";
  const cleanPath = String(path || "").replace(/^\/+/, "");
  return `${base}/${cleanPath}`;
}

function imageUrl(path) {
  if (!path) return "";
  return publicUrl(String(path).replace(/^\.?\//, ""));
}

function normalizeData(json, fileName) {
  const values = Array.isArray(json) ? json : Object.values(json || {});
  return values
    .filter(Boolean)
    .map((entry) => ({
      ...entry,
      _source: fileName,
      total: Number(entry.total || 0),
    }));
}

function average(values) {
  const numbers = values.map(Number).filter(Number.isFinite);
  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : 0;
}

function firstText(items, field) {
  return (
    items
      .map((item) => item[field])
      .find((value) => value !== undefined && value !== null && String(value).trim() !== "") || ""
  );
}

function entryKey(entry) {
  return String(entry.message_id || entry.id || entry.image || entry.title || "");
}

function combineEntries(entries) {
  const groups = new Map();

  entries.forEach((entry, index) => {
    const key = entryKey(entry) || `entry-${index}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  });

  return Array.from(groups.values()).map((items, primaryIndex) => {
    const raw_scores = {};
    const weighted_scores = {};

    scoreKeys.forEach((key) => {
      raw_scores[key] = average(items.map((item) => item.raw_scores?.[key]));
      weighted_scores[key] = average(items.map((item) => item.weighted_scores?.[key]));
    });

    const judgeTotals = items.map((item) => Number(item.total || 0));
    const sources = items.map((item) => item._source).filter(Boolean);
    const notes = items.map((item) => item.notes).filter(Boolean).join(" | ");

    return {
      ...items[0],
      message_id: firstText(items, "message_id"),
      title: firstText(items, "title") || "Untitled",
      creator: firstText(items, "creator"),
      game: firstText(items, "game"),
      resolution: firstText(items, "resolution"),
      location_track_map: firstText(items, "location_track_map"),
      short_description: firstText(items, "short_description"),
      image: firstText(items, "image"),
      raw_scores,
      weighted_scores,
      total: average(judgeTotals),
      notes,
      _sources: sources,
      _judgeCount: items.length,
      _judgeTotals: judgeTotals,
      _judges: items,
      _primaryIndex: primaryIndex,
    };
  });
}

function tieBreakersByEntry(entries) {
  const tieEntries = combineEntries(entries);
  const tieBreakers = new Map();

  tieEntries.forEach((entry) => {
    tieBreakers.set(entryKey(entry), {
      total: entry.total,
      _sources: entry._sources,
      _judgeCount: entry._judgeCount,
      _judgeTotals: entry._judgeTotals,
      _judges: entry._judges,
    });
  });

  return tieBreakers;
}

function attachTieBreakers(entries, tieBreakers) {
  return entries.map((entry) => ({
    ...entry,
    _tieBreaker: tieBreakers.get(entryKey(entry)),
  }));
}

function sortEntries(entries, sort) {
  const rows = [...entries];
  rows.sort((a, b) => {
    if (sort === "total-asc") return a.total - b.total || a._primaryIndex - b._primaryIndex;
    if (sort === "title-asc") return String(a.title || "").localeCompare(String(b.title || ""));
    if (sort === "game-asc") return String(a.game || "").localeCompare(String(b.game || ""));
    return b.total - a.total || a._primaryIndex - b._primaryIndex;
  });
  return rows;
}

function ScoreBar({ label, value }) {
  const cleanValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const percent = Math.max(0, Math.min(100, cleanValue * 10));

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "160px 1fr 52px" },
        alignItems: "center",
        gap: 1.25,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Box
        sx={{
          height: 9,
          borderRadius: 999,
          bgcolor: "rgba(255,255,255,0.08)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${percent}%`,
            height: "100%",
            borderRadius: 999,
            background: "linear-gradient(90deg, #ff1744, #00e5ff)",
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 900 }}>
        {cleanValue.toFixed(1)}/10
      </Typography>
    </Box>
  );
}

function ResultCard({ entry, rank }) {
  const [imageMissing, setImageMissing] = React.useState(false);
  const [imageOpen, setImageOpen] = React.useState(false);
  const [judgesOpen, setJudgesOpen] = React.useState(false);
  const fullImageUrl = imageUrl(entry.image);
  const sourceText =
    entry._sources?.length > 0
      ? `${entry._judgeCount} judge${entry._judgeCount === 1 ? "" : "s"}`
      : entry._source;

  return (
    <Paper
      component="article"
      sx={{
        overflow: "hidden",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "rgba(0, 12, 32, 0.82)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: 230, md: 310 },
          maxHeight: { xs: "70vh", md: 560 },
          bgcolor: "#050816",
        }}
      >
        <Chip
          label={`#${rank}`}
          size="small"
          icon={<EmojiEventsIcon />}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 1,
            fontWeight: 900,
            bgcolor: "rgba(0,0,0,0.68)",
            color: "common.white",
          }}
        />
        {imageMissing ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", p: 3 }}>
            <PhotoCameraIcon color="disabled" />
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              Image not found
            </Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {entry.image}
            </Typography>
          </Stack>
        ) : (
          <Box
            component="button"
            type="button"
            aria-label={`Open ${entry.title} full screen`}
            onClick={() => setImageOpen(true)}
            sx={{
              appearance: "none",
              border: 0,
              p: 0,
              m: 0,
              bgcolor: "transparent",
              cursor: "zoom-in",
              display: "block",
              maxWidth: "100%",
              maxHeight: { xs: "70vh", md: 560 },
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={fullImageUrl}
              alt={entry.title}
              loading="lazy"
              onError={() => setImageMissing(true)}
              sx={{
                width: "auto",
                height: "auto",
                maxWidth: "100%",
                maxHeight: { xs: "70vh", md: 560 },
                objectFit: "contain",
                display: "block",
                transition: "transform 180ms ease, filter 180ms ease",
                "&:hover": {
                  filter: "brightness(1.06)",
                  transform: "scale(1.01)",
                },
              }}
            />
          </Box>
        )}
      </Box>

      <Modal open={imageOpen} onClose={() => setImageOpen(false)} closeAfterTransition>
        <Fade in={imageOpen} timeout={220}>
          <Box
            onClick={() => setImageOpen(false)}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.92)",
              p: { xs: 1.5, sm: 3 },
              cursor: "zoom-out",
              "@keyframes resultImageZoomIn": {
                from: { opacity: 0, transform: "scale(0.94)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          >
            <IconButton
              aria-label="Close full screen image"
              onClick={(event) => {
                event.stopPropagation();
                setImageOpen(false);
              }}
              sx={{
                position: "absolute",
                top: { xs: 10, sm: 18 },
                right: { xs: 10, sm: 18 },
                zIndex: 1,
                color: "common.white",
                bgcolor: "rgba(0,0,0,0.52)",
                border: "1px solid rgba(255,255,255,0.22)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box
              component="img"
              src={fullImageUrl}
              alt={entry.title}
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: "auto",
                height: "auto",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
                boxShadow: "0 28px 90px rgba(0,0,0,0.58)",
                animation: "resultImageZoomIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                cursor: "default",
              }}
            />
          </Box>
        </Fade>
      </Modal>

      <Stack spacing={1.75} sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {entry.title}
            </Typography>
            {entry.creator && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
                By: {entry.creator}
              </Typography>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" label={entry.game || "Unknown game"} />
              <Chip size="small" label={entry.resolution || "Unknown resolution"} />
              <Chip size="small" label={entry.location_track_map || "Unknown location"} />
              <Chip size="small" label={sourceText || "Score file"} />
            </Stack>
          </Box>

          <Box
            sx={{
              minWidth: 88,
              borderRadius: 1,
              border: "1px solid rgba(34,197,94,0.28)",
              bgcolor: "rgba(34,197,94,0.1)",
              p: 1.25,
              textAlign: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1 }}>
              {entry.total.toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              / 100 avg
            </Typography>
          </Box>
        </Stack>

        {entry.short_description && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {entry.short_description}
          </Typography>
        )}

        <Stack spacing={1.1}>
          {scoreKeys.map((key) => (
            <ScoreBar key={key} label={SCORE_LABELS[key]} value={entry.raw_scores?.[key]} />
          ))}
        </Stack>

        {entry._judges?.length > 1 && (
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid rgba(245,158,11,0.28)",
              bgcolor: "rgba(245,158,11,0.08)",
              overflow: "hidden",
            }}
          >
            <Button
              fullWidth
              onClick={() => setJudgesOpen((open) => !open)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: judgesOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 160ms ease",
                  }}
                />
              }
              sx={{
                justifyContent: "space-between",
                color: "warning.light",
                textAlign: "left",
                p: 1.25,
                borderRadius: 0,
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              <Box component="span">
                Judge totals:{" "}
                {entry._judges
                  .map((judge, index) => `Judge ${index + 1}: ${Number(judge.total || 0).toFixed(1)}`)
                  .join(" | ")}
              </Box>
            </Button>

            <Collapse in={judgesOpen} timeout="auto" unmountOnExit>
              <Stack spacing={1.5} sx={{ px: 1.25, pb: 1.25 }}>
                {entry._judges.map((judge, index) => (
                  <Box
                    key={`${judge._source || "judge"}-${index}`}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: "1px solid rgba(255,255,255,0.1)",
                      bgcolor: "rgba(0,0,0,0.16)",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 900 }}>
                        Judge {index + 1}
                      </Typography>
                      <Typography variant="caption" color="warning.light" sx={{ fontWeight: 900 }}>
                        {Number(judge.total || 0).toFixed(1)} / 100
                      </Typography>
                    </Stack>
                    <Stack spacing={0.9}>
                      {scoreKeys.map((key) => (
                        <ScoreBar
                          key={key}
                          label={SCORE_LABELS[key]}
                          value={judge.raw_scores?.[key]}
                        />
                      ))}
                    </Stack>
                    {judge.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {judge.notes}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {entry._tieBreaker && (
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid rgba(0,229,255,0.28)",
              bgcolor: "rgba(0,229,255,0.08)",
              p: 1.25,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography variant="caption" color="primary.light" sx={{ fontWeight: 900 }}>
                  Tie-breaker only
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Displayed for the 3rd/4th tie and excluded from the main ranking score.
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1 }}>
                  {entry._tieBreaker.total.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / 100 tie avg
                </Typography>
              </Box>
            </Stack>
            <Typography variant="caption" color="primary.light" sx={{ display: "block", mt: 1 }}>
              Tie judges:{" "}
              {entry._tieBreaker._judges
                .map((judge, index) => `Judge ${index + 1}: ${Number(judge.total || 0).toFixed(1)}`)
                .join(" | ")}
            </Typography>
          </Box>
        )}

        {entry.notes && (
          <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
            {entry.notes}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

export default function ImageResults() {
  const [entries, setEntries] = React.useState([]);
  const [errors, setErrors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState("total-desc");

  React.useEffect(() => {
    let active = true;

    async function loadScores() {
      setLoading(true);
      const primaryImported = [];
      const tieBreakerImported = [];
      const nextErrors = [];

      for (const fileName of PRIMARY_SCORE_FILES) {
        try {
          const response = await fetch(publicUrl(fileName), { cache: "no-store" });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const json = await response.json();
          primaryImported.push(...normalizeData(json, fileName.split("/").pop()));
        } catch (error) {
          nextErrors.push(`${fileName}: ${error.message || String(error)}`);
        }
      }

      for (const fileName of TIE_BREAKER_SCORE_FILES) {
        try {
          const response = await fetch(publicUrl(fileName), { cache: "no-store" });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const json = await response.json();
          tieBreakerImported.push(...normalizeData(json, fileName.split("/").pop()));
        } catch (error) {
          nextErrors.push(`${fileName}: ${error.message || String(error)}`);
        }
      }

      if (!active) return;
      const primaryEntries = combineEntries(primaryImported);
      setEntries(attachTieBreakers(primaryEntries, tieBreakersByEntry(tieBreakerImported)));
      setErrors(nextErrors);
      setLoading(false);
    }

    loadScores();

    return () => {
      active = false;
    };
  }, []);

  const rows = React.useMemo(() => {
    const cleanQuery = query.toLowerCase().trim();
    const filtered = entries.filter((entry) => {
      if (!cleanQuery) return true;
      return [entry.title, entry.creator, entry.game, entry.location_track_map, entry.short_description]
        .some((value) => String(value || "").toLowerCase().includes(cleanQuery));
    });
    return sortEntries(filtered, sort);
  }, [entries, query, sort]);

  const averageScore = entries.length ? average(entries.map((entry) => entry.total)) : 0;
  const topScore = entries.length ? Math.max(...entries.map((entry) => entry.total)) : 0;

  return (
    <Stack spacing={2.5}>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(255,23,68,0.14), rgba(0,229,255,0.1) 45%, rgba(0,12,32,0.86))",
        }}
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip icon={<PhotoCameraIcon />} label="Virtual Photography Results" color="primary" />
          <Chip label="Judged results" variant="outlined" />
        </Stack>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 950, mb: 1 }}>
          Abomination Virtual Photography Challenge
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 820, lineHeight: 1.7 }}>
          Explore the final standings from the latest virtual photography challenge, with each
          entry ranked by its overall judging score. Tie-breaker judging is displayed only where
          needed and does not change the primary standings.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1.5,
            mt: 3,
          }}
        >
          {[
            ["Entries", entries.length],
            ["Average score", averageScore.toFixed(1)],
            ["Top score", topScore.toFixed(1)],
            ["Score files", `${PRIMARY_SCORE_FILES.length} + ${TIE_BREAKER_SCORE_FILES.length}`],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                p: 1.75,
                borderRadius: 1,
                border: "1px solid rgba(255,255,255,0.12)",
                bgcolor: "rgba(255,255,255,0.055)",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 950 }}>
                {value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {errors.length > 0 && (
        <Alert severity="warning">
          Some results could not be loaded: {errors.join("; ")}
        </Alert>
      )}

      <Paper
        sx={{
          p: 1.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(0,12,32,0.74)",
          position: { md: "sticky" },
          top: { md: 76 },
          zIndex: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontWeight: 900 }}>Results</Typography>
            <Typography variant="caption" color="text.secondary">
              Showing {rows.length} of {entries.length} judged entries.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { md: 560 } }}>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search title, game, location..."
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: { sm: 210 } }}>
              <Select
                value={sort}
                onChange={(event) => setSort(event.target.value)}
                startAdornment={<SortIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />}
              >
                <MenuItem value="total-desc">Highest score first</MenuItem>
                <MenuItem value="total-asc">Lowest score first</MenuItem>
                <MenuItem value="title-asc">Title A-Z</MenuItem>
                <MenuItem value="game-asc">Game A-Z</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<ClearAllIcon />}
              onClick={() => {
                setQuery("");
                setSort("total-desc");
              }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Divider />

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading competition results...
          </Typography>
        </Stack>
      ) : rows.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {rows.map((entry, index) => (
            <ResultCard key={entry.message_id || entry.image || entry.title} entry={entry} rank={index + 1} />
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 1 }}>
          <Typography color="text.secondary">
            {entries.length ? "No results match your search." : "No score entries found."}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
